import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  Shield, 
  AlertTriangle, 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  User,
  Phone,
  Mail,
  Calendar,
  Eye,
  Edit,
  Trash2,
  Download,
  Upload,
  Search,
  Filter,
  Bell,
  Users,
  BookOpen,
  Lock,
  ClipboardCheck,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const CONCERN_TYPES = [
  { value: 'physical_abuse', label: 'Physical Abuse', severity: 'high' },
  { value: 'emotional_abuse', label: 'Emotional Abuse', severity: 'high' },
  { value: 'neglect', label: 'Neglect', severity: 'high' },
  { value: 'sexual_abuse', label: 'Sexual Abuse', severity: 'critical' },
  { value: 'domestic_violence', label: 'Domestic Violence', severity: 'high' },
  { value: 'self_harm', label: 'Self-Harm Risk', severity: 'high' },
  { value: 'bullying', label: 'Bullying', severity: 'medium' },
  { value: 'online_safety', label: 'Online Safety Concern', severity: 'medium' },
  { value: 'welfare_concern', label: 'General Welfare Concern', severity: 'low' },
  { value: 'disclosure', label: 'Child Disclosure', severity: 'high' },
  { value: 'other', label: 'Other', severity: 'medium' }
];

const REFERRAL_AGENCIES = [
  'Local Authority Designated Officer (LADO)',
  'Children\'s Social Care',
  'Police',
  'CAMHS',
  'School Safeguarding Lead',
  'GP/Health Visitor',
  'NSPCC',
  'Other'
];

export default function SafeguardingPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [concerns, setConcerns] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [dbsRecords, setDbsRecords] = useState([]);
  const [trainingRecords, setTrainingRecords] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  
  // Dialogs
  const [concernDialogOpen, setConcernDialogOpen] = useState(false);
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);
  const [viewConcernDialog, setViewConcernDialog] = useState(null);
  const [dbsDialogOpen, setDbsDialogOpen] = useState(false);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [viewPolicyDialog, setViewPolicyDialog] = useState(null);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [safeguardingPolicies, setSafeguardingPolicies] = useState([]);
  const [canManagePolicies, setCanManagePolicies] = useState(false);
  const [canViewDBS, setCanViewDBS] = useState(false);
  
  // Policy form
  const [policyForm, setPolicyForm] = useState({
    title: '',
    category: 'safeguarding',
    content: '',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0]
  });
  
  // Form state
  const [concernForm, setConcernForm] = useState({
    patient_id: '',
    concern_type: '',
    description: '',
    witnesses: '',
    immediate_actions: '',
    reported_to: '',
    referred_to: ''
  });
  
  const [incidentForm, setIncidentForm] = useState({
    incident_type: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    location: '',
    description: '',
    persons_involved: '',
    action_taken: '',
    follow_up_required: false
  });
  
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isSafeguardingLead = user?.role === 'director' || user?.is_safeguarding_lead || isSuperAdmin;
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [concernsRes, incidentsRes, patientsRes, staffRes, dbsAccessRes, policiesRes, permRes] = await Promise.all([
        api.get('/safeguarding/concerns').catch(() => ({ data: [] })),
        api.get('/safeguarding/incidents').catch(() => ({ data: [] })),
        api.get('/patients').catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
        api.get('/safeguarding/check-dbs-access').catch(() => ({ data: { can_view_dbs: false } })),
        api.get('/safeguarding/policies').catch(() => ({ data: [] })),
        api.get('/safeguarding/check-policy-permissions').catch(() => ({ data: { can_manage: false } }))
      ]);
      setConcerns(concernsRes.data || []);
      setIncidents(incidentsRes.data || []);
      setPatients(patientsRes.data || []);
      setStaff(staffRes.data || []);
      setCanViewDBS(dbsAccessRes.data?.can_view_dbs || false);
      setSafeguardingPolicies(policiesRes.data || []);
      setCanManagePolicies(permRes.data?.can_manage || false);
      
      // Fetch DBS records only if user has permission
      if (dbsAccessRes.data?.can_view_dbs) {
        const dbsRes = await api.get('/safeguarding/dbs-records').catch(() => ({ data: [] }));
        setDbsRecords(dbsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch safeguarding data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleConcernSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/safeguarding/concerns', concernForm);
      toast.success('Safeguarding concern recorded');
      setConcernDialogOpen(false);
      setConcernForm({
        patient_id: '',
        concern_type: '',
        description: '',
        witnesses: '',
        immediate_actions: '',
        reported_to: '',
        referred_to: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record concern');
    }
  };
  
  const handleIncidentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/safeguarding/incidents', incidentForm);
      toast.success('Incident recorded');
      setIncidentDialogOpen(false);
      setIncidentForm({
        incident_type: '',
        date: new Date().toISOString().split('T')[0],
        time: '',
        location: '',
        description: '',
        persons_involved: '',
        action_taken: '',
        follow_up_required: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record incident');
    }
  };
  
  const updateConcernStatus = async (concernId, status) => {
    try {
      await api.put(`/safeguarding/concerns/${concernId}/status`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPolicy) {
        await api.put(`/safeguarding/policies/${editingPolicy.id}`, policyForm);
        toast.success('Policy updated');
      } else {
        await api.post('/safeguarding/policies', policyForm);
        toast.success('Policy added');
      }
      setPolicyDialogOpen(false);
      setEditingPolicy(null);
      setPolicyForm({ title: '', category: 'safeguarding', content: '', version: '1.0', effective_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    }
  };
  
  const handleDeletePolicy = async (policyId) => {
    if (!confirm('Are you sure you want to delete this policy?')) return;
    try {
      await api.delete(`/safeguarding/policies/${policyId}`);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete policy');
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-700';
      case 'investigating': return 'bg-yellow-100 text-yellow-700';
      case 'referred': return 'bg-blue-100 text-blue-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };
  
  // Statistics
  const openConcerns = concerns.filter(c => c.status === 'open' || c.status === 'investigating').length;
  const criticalConcerns = concerns.filter(c => {
    const type = CONCERN_TYPES.find(t => t.value === c.concern_type);
    return type?.severity === 'critical' || type?.severity === 'high';
  }).length;
  const pendingDBS = dbsRecords.filter(d => {
    if (!d.expiry_date) return false;
    const expiry = new Date(d.expiry_date);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiry <= thirtyDaysFromNow;
  }).length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="safeguarding-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Safeguarding</h1>
          <p className="text-slate-500">Child protection and safeguarding management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setConcernDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Report Concern
          </Button>
          <Button onClick={() => setIncidentDialogOpen(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Log Incident
          </Button>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Open Concerns</p>
                <p className="text-3xl font-bold text-red-700">{openConcerns}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">High Priority</p>
                <p className="text-3xl font-bold text-orange-700">{criticalConcerns}</p>
              </div>
              <Bell className="w-10 h-10 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Incidents</p>
                <p className="text-3xl font-bold text-blue-700">{incidents.length}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">DBS Expiring</p>
                <p className="text-3xl font-bold text-amber-700">{pendingDBS}</p>
              </div>
              <Shield className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main Content */}
      <Tabs defaultValue="concerns" className="space-y-4">
        <TabsList>
          <TabsTrigger value="concerns" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Concerns ({concerns.length})
          </TabsTrigger>
          <TabsTrigger value="incidents" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Incidents ({incidents.length})
          </TabsTrigger>
          {canViewDBS && (
            <TabsTrigger value="dbs" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              DBS Records
            </TabsTrigger>
          )}
          <TabsTrigger value="training" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Training
          </TabsTrigger>
          <TabsTrigger value="policies" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Policies
          </TabsTrigger>
        </TabsList>
        
        {/* Concerns Tab */}
        <TabsContent value="concerns">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Safeguarding Concerns
              </CardTitle>
              <CardDescription>Active and historical safeguarding concerns</CardDescription>
            </CardHeader>
            <CardContent>
              {concerns.length === 0 ? (
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No concerns recorded</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Child</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reported By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {concerns.map((concern) => {
                      const concernType = CONCERN_TYPES.find(t => t.value === concern.concern_type);
                      return (
                        <TableRow key={concern.id}>
                          <TableCell>{new Date(concern.created_at).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell className="font-medium">{concern.patient_name}</TableCell>
                          <TableCell>{concernType?.label || concern.concern_type}</TableCell>
                          <TableCell>
                            <Badge className={getSeverityColor(concernType?.severity)}>
                              {concernType?.severity?.toUpperCase()}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(concern.status)}>
                              {concern.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{concern.reported_by_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setViewConcernDialog(concern)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isSafeguardingLead && concern.status !== 'closed' && (
                                <Select onValueChange={(v) => updateConcernStatus(concern.id, v)}>
                                  <SelectTrigger className="w-[120px] h-8">
                                    <SelectValue placeholder="Update" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="investigating">Investigating</SelectItem>
                                    <SelectItem value="referred">Referred</SelectItem>
                                    <SelectItem value="resolved">Resolved</SelectItem>
                                    <SelectItem value="closed">Close</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Incidents Tab */}
        <TabsContent value="incidents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Incident Log
              </CardTitle>
              <CardDescription>Health & safety and welfare incidents</CardDescription>
            </CardHeader>
            <CardContent>
              {incidents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No incidents recorded</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Follow-up</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {incidents.map((incident) => (
                      <TableRow key={incident.id}>
                        <TableCell>{new Date(incident.date).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell className="font-medium">{incident.incident_type}</TableCell>
                        <TableCell>{incident.location}</TableCell>
                        <TableCell className="max-w-xs truncate">{incident.description}</TableCell>
                        <TableCell>
                          {incident.follow_up_required ? (
                            <Badge className="bg-amber-100 text-amber-700">Required</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">Complete</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* DBS Records Tab - Only shown to authorized users */}
        {canViewDBS && (
          <TabsContent value="dbs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-[#1e3a5f]" />
                    DBS Records
                  </CardTitle>
                  <CardDescription>Staff DBS check records and compliance - Restricted Access</CardDescription>
                </div>
                {isSuperAdmin && (
                  <Button onClick={() => setDbsDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add DBS Record
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <Lock className="w-4 h-4 inline mr-2" />
                    <strong>Restricted Access:</strong> DBS records are confidential and only visible to Directors, HR, and Safeguarding Leads. Parents/Carers cannot access this information.
                  </p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead>DBS Number</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staff.map((member) => {
                      const dbs = dbsRecords.find(d => d.staff_id === member.id);
                      const isExpiring = dbs?.expiry_date && new Date(dbs.expiry_date) <= new Date(Date.now() + 30*24*60*60*1000);
                      const isExpired = dbs?.expiry_date && new Date(dbs.expiry_date) < new Date();
                      
                      return (
                        <TableRow key={member.id}>
                          <TableCell className="font-medium">{member.first_name} {member.last_name}</TableCell>
                          <TableCell>{dbs?.dbs_number || '-'}</TableCell>
                          <TableCell>{dbs?.dbs_type || '-'}</TableCell>
                          <TableCell>{dbs?.issue_date ? new Date(dbs.issue_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                          <TableCell>{dbs?.expiry_date ? new Date(dbs.expiry_date).toLocaleDateString('en-GB') : '-'}</TableCell>
                          <TableCell>
                            {!dbs ? (
                              <Badge className="bg-red-100 text-red-700">Not Recorded</Badge>
                            ) : isExpired ? (
                              <Badge className="bg-red-100 text-red-700">Expired</Badge>
                            ) : isExpiring ? (
                              <Badge className="bg-amber-100 text-amber-700">Expiring Soon</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Valid</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Training Tab */}
        <TabsContent value="training">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-500" />
                Safeguarding Training
              </CardTitle>
              <CardDescription>Staff safeguarding training records</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Safeguarding training records</p>
                <p className="text-sm text-slate-400">All staff must complete Level 1 Safeguarding training</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Policies Tab */}
        <TabsContent value="policies">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-slate-600" />
                  Safeguarding Policies
                </CardTitle>
                <CardDescription>Child protection policies and procedures</CardDescription>
              </div>
              {(isSuperAdmin || canManagePolicies) && (
                <Button onClick={() => setPolicyDialogOpen(true)} className="bg-[#1e3a5f]">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Policy
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Custom Uploaded Policies */}
                {safeguardingPolicies.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-slate-700 mb-2">Uploaded Policies</h4>
                    {safeguardingPolicies.map((policy) => (
                      <div key={policy.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 mb-2 border border-blue-200">
                        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setViewPolicyDialog(policy)}>
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="font-medium">{policy.title}</span>
                            <p className="text-xs text-slate-500">v{policy.version} | {policy.category} | {new Date(policy.effective_date).toLocaleDateString('en-GB')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setViewPolicyDialog(policy)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(isSuperAdmin || canManagePolicies) && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => {
                                setEditingPolicy(policy);
                                setPolicyForm({
                                  title: policy.title,
                                  category: policy.category,
                                  content: policy.content,
                                  version: policy.version,
                                  effective_date: policy.effective_date
                                });
                                setPolicyDialogOpen(true);
                              }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeletePolicy(policy.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Default Template Policies */}
                <h4 className="font-medium text-slate-700 mb-2">Standard Templates</h4>
                {[
                  'Child Protection Policy',
                  'Safeguarding Children Policy',
                  'Whistleblowing Policy',
                  'Safer Recruitment Policy',
                  'E-Safety Policy',
                  'Anti-Bullying Policy',
                  'Prevent Duty Policy'
                ].map((policy, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#1e3a5f]" />
                      <span>{policy}</span>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Add/Edit Policy Dialog */}
      <Dialog open={policyDialogOpen} onOpenChange={(open) => {
        setPolicyDialogOpen(open);
        if (!open) {
          setEditingPolicy(null);
          setPolicyForm({ title: '', category: 'safeguarding', content: '', version: '1.0', effective_date: new Date().toISOString().split('T')[0] });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Add Safeguarding Policy'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePolicySubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Policy Title *</Label>
                <Input
                  value={policyForm.title}
                  onChange={(e) => setPolicyForm({...policyForm, title: e.target.value})}
                  placeholder="e.g., Child Protection Policy"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={policyForm.category} onValueChange={(v) => setPolicyForm({...policyForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="safeguarding">Safeguarding</SelectItem>
                    <SelectItem value="child_protection">Child Protection</SelectItem>
                    <SelectItem value="recruitment">Safer Recruitment</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="reporting">Reporting</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Version</Label>
                <Input
                  value={policyForm.version}
                  onChange={(e) => setPolicyForm({...policyForm, version: e.target.value})}
                  placeholder="1.0"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={policyForm.effective_date}
                  onChange={(e) => setPolicyForm({...policyForm, effective_date: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Policy Content *</Label>
              <Textarea
                value={policyForm.content}
                onChange={(e) => setPolicyForm({...policyForm, content: e.target.value})}
                rows={10}
                placeholder="Enter the full policy text..."
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPolicyDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">{editingPolicy ? 'Update Policy' : 'Add Policy'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* View Policy Dialog */}
      <Dialog open={!!viewPolicyDialog} onOpenChange={() => setViewPolicyDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewPolicyDialog?.title}</DialogTitle>
          </DialogHeader>
          {viewPolicyDialog && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <Badge variant="outline">v{viewPolicyDialog.version}</Badge>
                <span className="capitalize">{viewPolicyDialog.category}</span>
                <span>Effective: {new Date(viewPolicyDialog.effective_date).toLocaleDateString('en-GB')}</span>
              </div>
              <div className="prose prose-slate max-w-none">
                <div className="whitespace-pre-wrap bg-slate-50 p-4 rounded-lg">
                  {viewPolicyDialog.content || 'No content available.'}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewPolicyDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Report Concern Dialog */}
      <Dialog open={concernDialogOpen} onOpenChange={setConcernDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Report Safeguarding Concern
            </DialogTitle>
            <DialogDescription>
              Record details of a safeguarding concern. This will be reviewed by the Designated Safeguarding Lead.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConcernSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Child *</Label>
                <Select value={concernForm.patient_id} onValueChange={(v) => setConcernForm({...concernForm, patient_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select child" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type of Concern *</Label>
                <Select value={concernForm.concern_type} onValueChange={(v) => setConcernForm({...concernForm, concern_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONCERN_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description of Concern *</Label>
              <Textarea 
                value={concernForm.description}
                onChange={(e) => setConcernForm({...concernForm, description: e.target.value})}
                rows={4}
                placeholder="Describe what you have seen, heard, or been told. Use the child's own words where possible."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Witnesses</Label>
              <Input 
                value={concernForm.witnesses}
                onChange={(e) => setConcernForm({...concernForm, witnesses: e.target.value})}
                placeholder="Names of any witnesses"
              />
            </div>
            <div className="space-y-2">
              <Label>Immediate Actions Taken</Label>
              <Textarea 
                value={concernForm.immediate_actions}
                onChange={(e) => setConcernForm({...concernForm, immediate_actions: e.target.value})}
                rows={2}
                placeholder="What actions have you taken so far?"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reported To</Label>
                <Input 
                  value={concernForm.reported_to}
                  onChange={(e) => setConcernForm({...concernForm, reported_to: e.target.value})}
                  placeholder="Name of person reported to"
                />
              </div>
              <div className="space-y-2">
                <Label>Referred To (if applicable)</Label>
                <Select value={concernForm.referred_to} onValueChange={(v) => setConcernForm({...concernForm, referred_to: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                  <SelectContent>
                    {REFERRAL_AGENCIES.map(agency => (
                      <SelectItem key={agency} value={agency}>{agency}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConcernDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">Submit Concern</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Log Incident Dialog */}
      <Dialog open={incidentDialogOpen} onOpenChange={setIncidentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Incident</DialogTitle>
            <DialogDescription>
              Record a health & safety or welfare incident
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIncidentSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Incident Type *</Label>
                <Select value={incidentForm.incident_type} onValueChange={(v) => setIncidentForm({...incidentForm, incident_type: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accident">Accident</SelectItem>
                    <SelectItem value="injury">Injury</SelectItem>
                    <SelectItem value="near_miss">Near Miss</SelectItem>
                    <SelectItem value="behavioral">Behavioral</SelectItem>
                    <SelectItem value="medical">Medical</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={incidentForm.date}
                  onChange={(e) => setIncidentForm({...incidentForm, date: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Location *</Label>
              <Input 
                value={incidentForm.location}
                onChange={(e) => setIncidentForm({...incidentForm, location: e.target.value})}
                placeholder="Where did the incident occur?"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea 
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                rows={3}
                placeholder="Describe what happened"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Action Taken</Label>
              <Textarea 
                value={incidentForm.action_taken}
                onChange={(e) => setIncidentForm({...incidentForm, action_taken: e.target.value})}
                rows={2}
                placeholder="What action was taken?"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIncidentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Log Incident</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* View Concern Dialog */}
      <Dialog open={!!viewConcernDialog} onOpenChange={() => setViewConcernDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Concern Details</DialogTitle>
          </DialogHeader>
          {viewConcernDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Child</Label>
                  <p className="font-medium">{viewConcernDialog.patient_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Date Reported</Label>
                  <p>{new Date(viewConcernDialog.created_at).toLocaleDateString('en-GB')}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Type</Label>
                  <p>{CONCERN_TYPES.find(t => t.value === viewConcernDialog.concern_type)?.label}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <Badge className={getStatusColor(viewConcernDialog.status)}>{viewConcernDialog.status}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Description</Label>
                <p className="bg-slate-50 p-3 rounded mt-1">{viewConcernDialog.description}</p>
              </div>
              {viewConcernDialog.immediate_actions && (
                <div>
                  <Label className="text-xs text-slate-500">Immediate Actions</Label>
                  <p className="bg-slate-50 p-3 rounded mt-1">{viewConcernDialog.immediate_actions}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewConcernDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
